/**
 * AdminSettings — full-width admin overhaul.
 * Top-tabs: Gebruikersbeheer, Applicatiebeheer, Logging, Algemene mailboxen, Feedback.
 * Per tab: AdminHub met sidebar + card-grid menu (HubPage-stijl). Feedback heeft
 * geen sub-secties en rendert direct het backlogbord.
 *
 * Deeplinks:
 *   ?tab=users|app|logging|mailboxen|feedback
 *   ?section=<sub-key>   (alleen relevant voor users/app)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { SettingsProvider, T, Icon, useSettings } from './adminSettingsCommon';
import AdminHub from './AdminHub';
import PageHeader from '../../ui-components/PageHeader';
import { useUserRole } from '../../contexts/UserRoleContext';

// Existing sub-sections
import UserManagement from './UserManagement';
import EmployeeOnboarding from './EmployeeOnboarding';
import { BacklogBoard } from '../Backlog/Backlog';
import CapabilityGaps from './CapabilityGaps';
import AdminShortcuts from './AdminShortcuts';

// New sub-sections (extracted from old inline tabs)
import AIModels from './sections/AIModels';
import AITegoed from './sections/AITegoed';
import AIPrompts from './sections/AIPrompts';
import ScriptBreakdownConfig from './sections/ScriptBreakdownConfig';
import RenderServerJobs from './sections/RenderServerJobs';
import RenderServers from './sections/RenderServers';
import RenderUpdates from './sections/RenderUpdates';
import CorveeTasksConfig from './sections/CorveeTasksConfig';
import DashboardTemplates from './sections/DashboardTemplates';
import PlanningRegels from './sections/PlanningRegels';
import KlantAfkortingen from './sections/KlantAfkortingen';
import VeryVideoBureaus from './sections/VeryVideoBureaus';
import Portfolios from './sections/Portfolios';
import InplannenProducten from './sections/InplannenProducten';
import WijzigingsLog from './sections/WijzigingsLog';
import Paginagebruik from './sections/Paginagebruik';
import TaakgeneratieRegels from './sections/TaakgeneratieRegels';
import Scriptstijlen from './sections/Scriptstijlen';
import Taaksjablonen from './sections/Taaksjablonen';
import AchmeaTarieven from './sections/AchmeaTarieven';
import KlantVereisten from './sections/KlantVereisten';
import SlimUrenRegels from './sections/SlimUrenRegels';
import SharePointAnalysis from './sections/SharePointAnalysis';
import AppRoleMatrix from './sections/AppRoleMatrix';
import Groepen from './sections/Groepen';
import OpdrachtResync from './sections/OpdrachtResync';
import WikiKennis from './sections/WikiKennis';
import IntegratieStatus from './sections/IntegratieStatus';
import MailToegang from './sections/MailToegang';
import TeamsKanaalSjabloon from './sections/TeamsKanaalSjabloon';
import SatelliteWizard from './sections/SatelliteWizard';
import SatelliteApprovals from './sections/SatelliteApprovals';
import SatelliteUsage from './sections/SatelliteUsage';
import AgendaBlokkenBeheer from './sections/AgendaBlokkenBeheer';
import { makeLogPlaceholder } from './sections/LogPlaceholder';
import InfoMailbox from './algemeneMailboxen/InfoMailbox';
import IkwilwerkenMailbox from './algemeneMailboxen/IkwilwerkenMailbox';
import MailboxLog from './algemeneMailboxen/MailboxLog';
import { beperkteTabs } from './algemeneMailboxen/rechten';

// ── Section catalogues ─────────────────────────────────────────────────────

const USER_SECTIONS = [
  {
    key: 'roles', label: 'Gebruikers', icon: 'admin_panel_settings',
    desc: 'Beheer rollen, permissies, corvee-beschikbaarheid en synchronisatie van medewerkers vanuit Airtable & Gripp.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: UserManagement,
  },
  {
    key: 'onboarding', label: 'Medewerker onboarden', icon: 'rocket_launch',
    desc: 'Nieuwe medewerker aanmaken: Excel-mutatieformulier inlezen, Airtable + Gripp aanmaken, welkomstmail sturen.',
    accent: { color: '#10b981', bg: '#ecfdf5' },
    component: EmployeeOnboarding,
  },
  {
    key: 'groepen', label: 'Groepen', icon: 'groups',
    desc: 'Centrale groepen voor autorisatie, eerst het MT (o.a. beoordelaar van elke vacature). Wijzigingen worden vastgelegd.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: Groepen,
  },
  {
    key: 'apps-rollen', label: 'Apps & rollen', icon: 'apps',
    desc: 'Rol×app-toegangsmatrix: zet per rol per app of die zichtbaar/bewerkbaar is. Doorzoekbaar.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: AppRoleMatrix,
  },
  {
    key: 'agenda-blokken', label: 'Taken in agenda', icon: 'event_available',
    desc: 'Wie krijgt zijn ingeplande taken als blokken in Outlook. Per persoon aan/uit, met een proefrun.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: AgendaBlokkenBeheer,
  },
];

const APP_SECTIONS = [
  {
    key: 'satellite-wizard', label: 'Nieuwe tool (wizard)', icon: 'add_circle',
    desc: 'Beschrijf een tool in gewone taal; na goedkeuring bouwt de AI-agent hem en zet hem live achter FF-login.',
    accent: { color: '#6d28d9', bg: '#ede9fe' },
    component: SatelliteWizard,
  },
  {
    key: 'satellite-approvals', label: 'Satelliet-aanvragen', icon: 'verified',
    desc: 'Keur tool-aanvragen goed (start provisioning + AI-bouw) of wijs ze af.',
    accent: { color: '#6d28d9', bg: '#ede9fe' },
    component: SatelliteApprovals,
  },
  {
    key: 'satellite-usage', label: 'Satelliet-gebruik', icon: 'insights',
    desc: 'Kosten, tokengebruik en budgetstatus per satelliet-tool.',
    accent: { color: '#6d28d9', bg: '#ede9fe' },
    component: SatelliteUsage,
  },
  {
    key: 'wijzigingslog', label: 'Wijzigingslog', icon: 'history',
    desc: 'Wie zette welke taak of welk project wanneer op Afgerond — statuswijzigingen via de tool en de Gripp-sync.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: WijzigingsLog,
  },
  {
    key: 'klant-afkortingen', label: 'Klant-afkortingen', icon: 'short_text',
    desc: 'Korte weergavenamen (OMWB, KIWA) voor lange bedrijfsnamen in Dagstart en capaciteit.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: KlantAfkortingen,
  },
  {
    key: 'very-video-bureaus', label: 'Very Video-bureaus', icon: 'movie_filter',
    desc: 'Reclamebureaus (Full Frame Exclusive): zusje van ONLY of los bureau — voor het Very Video-blok en de branche in de Opdracht-mix.',
    accent: { color: '#9d174d', bg: '#fce7f3' },
    component: VeryVideoBureaus,
  },
  {
    key: 'portfolios', label: "Portfolio's", icon: 'folder_shared',
    desc: 'Klant → portfolio → client lead. Vier portfolio\'s (energie, bureaus, publiek/zorg, industrie/retail) — het filter in de Opdracht-mix.',
    accent: { color: '#4338ca', bg: '#e0e7ff' },
    component: Portfolios,
  },
  {
    key: 'inplannen-producten', label: 'Nog in te plannen', icon: 'pending_actions',
    desc: 'Welke offerte-producten tellen als montage, animatie en feedback, en de norm per werkdag.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: InplannenProducten,
  },
  {
    key: 'planning-regels', label: 'Planning-regels', icon: 'rule',
    desc: 'Planner-adres (Frits), capaciteitsregels en max draaidagen per persoon.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: PlanningRegels,
  },
  {
    key: 'slim-uren-regels', label: 'Slim Uren-regels', icon: 'timer',
    desc: 'Koppelregels voor uren schrijven — taak/afspraak → interne Gripp-regel; bijsturen via chat.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: SlimUrenRegels,
  },
  {
    key: 'taakgeneratie', label: 'Taakgeneratie', icon: 'checklist',
    desc: 'Model en regels voor "Slimme taken uit de offerte" — bijsturen via chat.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: TaakgeneratieRegels,
  },
  {
    key: 'scriptstijlen', label: 'Scriptstijlen', icon: 'edit_note',
    desc: 'Schrijfstijl per filmtype: richtlijnen, opbouw en de voorbeelden waaruit de generator leert.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: Scriptstijlen,
  },
  {
    key: 'taaksjablonen', label: 'Taaksjablonen', icon: 'account_tree',
    desc: 'Sjablonen (labels, taken, subtaken) waaruit de wizard en de generator taken maken — beheer in onze database.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: Taaksjablonen,
  },
  {
    key: 'achmea-tarieven', label: 'Achmea tarieven', icon: 'euro',
    desc: 'Achmea/Interpolis-tarievenkaart — gebruikt door factuur-export en offerte-check.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: AchmeaTarieven,
  },
  {
    key: 'klantvereisten', label: 'Klantvereisten', icon: 'verified_user',
    desc: 'Werkafspraken per klant (bv. Schneider Electric): NDA freelancers, AI-log, geen portfolio — en het AI-kwartaalrapport.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: KlantVereisten,
  },
  {
    key: 'teams-kanaal', label: 'Teams-kanaalsjabloon', icon: 'groups',
    desc: 'Mappen en vaste leden van het Teams-kanaal dat Toolframe bij een geaccepteerde offerte aanmaakt, plus wat de automaat deed.',
    accent: { color: '#4338ca', bg: '#e0e7ff' },
    component: TeamsKanaalSjabloon,
  },
  {
    key: 'mail-toegang', label: 'Mail & agenda-toegang', icon: 'mark_email_read',
    desc: 'Welke mailboxen mogen Frits en de overdrachtstool in? Ledenlijst + kant-en-klaar PowerShell-commando om iemand toe te voegen.',
    accent: { color: '#7c3aed', bg: '#ede9fe' },
    component: MailToegang,
  },
  {
    key: 'integratie-status', label: 'Integratie-status', icon: 'health_and_safety',
    desc: 'Werken Gripp, Airtable, OpenAI en de rest nog? Dagelijkse controle op verlopen sleutels, leeg tegoed en storingen.',
    accent: { color: '#0e7490', bg: '#cffafe' },
    component: IntegratieStatus,
  },
  {
    key: 'sharepoint-storage', label: 'SharePoint opslag', icon: 'storage',
    desc: 'Vind grote bestanden/mappen, PowerPoint-versie-opblaas en media die naar de NAS moet; oude versies opschonen.',
    accent: { color: '#ea580c', bg: '#ffedd5' },
    component: SharePointAnalysis,
  },
  {
    key: 'shortcuts', label: 'Dashboard shortcuts', icon: 'link',
    desc: 'Standaard shortcut-tegels die vooraan in elk gebruikersdashboard staan.',
    accent: { color: '#ec4899', bg: '#fce7f3' },
    component: AdminShortcuts,
  },
  {
    key: 'dashboard-templates', label: 'Dashboard templates', icon: 'dashboard_customize',
    desc: 'Voorpagina-templates ontwerpen en pushen naar gebruikers zonder eigen aanpassingen.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: DashboardTemplates,
  },
  {
    key: 'ai-models', label: 'AI modellen', icon: 'smart_toy',
    desc: 'Welk OpenAI-model elke feature gebruikt (Effie, Recorder, Callsheets, SRT).',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: AIModels,
  },
  {
    key: 'ai-tegoed', label: 'AI-tegoed', icon: 'savings',
    desc: 'Alarm als het tegoed bij OpenAI of Anthropic op is, dagelijkse saldo-check en waar bij te vullen.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: AITegoed,
  },
  {
    key: 'ai-prompts', label: 'AI prompts', icon: 'edit_note',
    desc: 'System prompts voor Effie-analyse, scene-breakdown en shotlist generatie.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: AIPrompts,
  },
  {
    key: 'ai-monitoring', label: 'AI monitoring', icon: 'monitoring',
    desc: 'Vragen die de AI-assistent niet kon beantwoorden — markeer als opgelost wanneer fixed.',
    accent: { color: '#f59e0b', bg: '#fef3c7' },
    component: CapabilityGaps,
  },
  {
    key: 'wiki-kennis', label: 'Wiki-kennis', icon: 'auto_stories',
    desc: 'Outline-wiki als kennisbron voor AI Chat, Assistant en Offertes: status, synchronisatie en zoektest.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: WikiKennis,
  },
  {
    key: 'script-breakdown', label: 'Script breakdown', icon: 'movie_filter',
    desc: 'Productieplanning rekenregels: max draaidag, base times, time modifiers, crew call.',
    accent: { color: '#3b82f6', bg: '#dbeafe' },
    component: ScriptBreakdownConfig,
  },
  {
    key: 'render-servers', label: 'Render servers', icon: 'dns',
    desc: 'Beheer en goedkeuring van render-server devices; bekijk capabilities, toegang en logs per apparaat.',
    accent: { color: '#7c3aed', bg: '#ede9fe' },
    component: RenderServers,
  },
  {
    key: 'corvee-tasks', label: 'Corvee taken', icon: 'cleaning_services',
    desc: 'Standaard takentemplate voor het corveerooster: dagtaken, weektaken en personen per dag.',
    accent: { color: '#16a34a', bg: '#dcfce7' },
    component: CorveeTasksConfig,
  },
  {
    key: 'opdracht-resync', label: 'Opdracht re-sync', icon: 'sync',
    desc: 'Draai de Gripp→Airtable-sync (status + deliverables) opnieuw voor één opdrachtnummer.',
    accent: { color: '#0f766e', bg: '#ccfbf1' },
    component: OpdrachtResync,
  },
];

const LOGGING_SECTIONS = [
  {
    key: 'paginagebruik', label: 'Paginagebruik', icon: 'query_stats',
    desc: 'Welke schermen worden gebruikt, door hoeveel mensen per dag en hoe lang — zonder namen, 180 dagen bewaard.',
    accent: { color: '#635983', bg: '#dbcdff' },
    component: Paginagebruik,
  },
  {
    key: 'render-logs', label: 'Render-server jobs', icon: 'history',
    desc: 'Alle jobs die de render-server gebruiken — naamtitels, narrowcasting, ondertitels, video-QA en Ritmo — in één filterbaar overzicht.',
    accent: { color: '#3b82f6', bg: '#dbeafe' },
    component: RenderServerJobs,
  },
  {
    key: 'render-updates', label: 'Render updates', icon: 'sync',
    desc: 'Nachtelijke en handmatige updates (git pull + herstart) per render-server, met resultaat en commit-versie.',
    accent: { color: '#7c3aed', bg: '#ede9fe' },
    component: RenderUpdates,
  },
  {
    key: 'recorder-logs', label: 'Recorder logs', icon: 'mic',
    desc: 'Transcripties en uploads van de meeting-recorder.',
    accent: { color: '#94a3b8', bg: '#f1f5f9' },
    component: makeLogPlaceholder('Recorder logs', 'Transcripties en uploads van de meeting-recorder.'),
  },
  {
    key: 'sales-mail-logs', label: 'Sales-mail logs', icon: 'mail',
    desc: 'Ingest van BCC sales@ mails en toewijzing aan prospects.',
    accent: { color: '#94a3b8', bg: '#f1f5f9' },
    component: makeLogPlaceholder('Sales-mail logs', 'Ingest van BCC sales@ mails en toewijzing aan prospects.'),
  },
];

// Algemene mailboxen (info@ + ikwilwerken@). Het ikwilwerken@-deel is ook zichtbaar met alleen het recht
// `werving` (zonder admin_settings); zie `beperkteTabs` (algemeneMailboxen/rechten.js). Engine: services/algmail_beheer.py.
const MAILBOX_SECTIONS = [
  {
    key: 'info', label: 'info@', icon: 'inbox',
    desc: 'Per categorie negeren, samenvatten of melden; PO-check, Gripp-schrijven en het PO-overzicht.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: InfoMailbox,
  },
  {
    key: 'ikwilwerken', label: 'ikwilwerken@', icon: 'work',
    desc: 'Standaardmails (sjablonen), wie de interne mail krijgt en concepten aan/uit.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: IkwilwerkenMailbox,
  },
  {
    key: 'log', label: 'Log', icon: 'history',
    desc: 'Laatste rondes, concepten, interne mails en PO-acties — alleen metadata.',
    accent: { color: '#526075', bg: '#d5e3fd' },
    component: MailboxLog,
  },
];

// De Feedback-tab liet een rij sub-tabs zien (Feedback | Pipeline | Backlog)
// waarvan er één werd gebruikt. Die rij is weg en de tab opent nu meteen het
// bord; de feedbacklijst en de pipeline-weergave zijn verwijderd.
const BacklogPaneel = () => <BacklogBoard embedded />;

const TOP_TABS = [
  {
    key: 'users', label: 'Gebruikersbeheer', icon: 'group',
    accent: { color: '#526075', bg: '#d5e3fd' },
    sections: USER_SECTIONS,
    sidebarTitle: 'Gebruikers',
    sidebarSubtitle: 'Rollen, rechten en onboarding van medewerkers.',
  },
  {
    key: 'app', label: 'Applicatiebeheer', icon: 'tune',
    accent: { color: '#635983', bg: '#dbcdff' },
    sections: APP_SECTIONS,
    sidebarTitle: 'Applicatie',
    sidebarSubtitle: 'AI-modellen, prompts, productie-engine en dashboard-defaults.',
  },
  {
    key: 'logging', label: 'Logging', icon: 'receipt_long',
    accent: { color: '#3b82f6', bg: '#dbeafe' },
    sections: LOGGING_SECTIONS,
    sidebarTitle: 'Logging',
    sidebarSubtitle: 'Audit-trails en metrics per tool. Hier groeien meer logs naartoe.',
  },
  {
    key: 'mailboxen', label: 'Algemene mailboxen', icon: 'mail',
    accent: { color: '#526075', bg: '#d5e3fd' },
    sections: MAILBOX_SECTIONS,
    sidebarTitle: 'Algemene mailboxen',
    sidebarSubtitle: 'info@ en ikwilwerken@: wat de tool leest, meldt en klaarzet. Er gaat nooit iets naar buiten.',
  },
  {
    key: 'feedback', label: 'Feedback', icon: 'forum',
    accent: { color: '#10b981', bg: '#ecfdf5' },
    sections: null,
    flat: BacklogPaneel,
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────

function getDeeplink() {
  const sp = new URLSearchParams(window.location.search);
  return { tab: sp.get('tab'), section: sp.get('section') };
}

function pushDeeplink(tab, section) {
  const sp = new URLSearchParams(window.location.search);
  sp.set('tab', tab);
  if (section) sp.set('section', section); else sp.delete('section');
  const url = `${window.location.pathname}?${sp.toString()}`;
  window.history.replaceState({}, '', url);
}

// ── Main ───────────────────────────────────────────────────────────────────

export default function AdminSettings() {
  return (
    <SettingsProvider>
      <AdminSettingsBody />
    </SettingsProvider>
  );
}

function AdminSettingsBody() {
  const { loading } = useSettings();
  const { permissions } = useUserRole();

  // Wie hier binnenkomt zonder admin_settings ziet alleen de tabs waar hij recht op heeft: Feedback
  // (feedback_beheer/backlog) en/of het ikwilwerken@-deel van Algemene mailboxen (werving).
  const isFullAdmin = !!permissions?.admin_settings?.view;
  const TABS = beperkteTabs(TOP_TABS, permissions);
  const beperkt = !isFullAdmin;

  const initial = useMemo(getDeeplink, []);
  const [zoek, setZoek] = useState('');
  const zoekTreffers = useMemo(() => {
    const q = zoek.trim().toLowerCase();
    if (!q) return [];
    const uit = [];
    TABS.forEach((t) => {
      if (t.sections) {
        t.sections.forEach((sec) => {
          if (`${sec.label} ${sec.desc || ''} ${sec.key}`.toLowerCase().includes(q)) uit.push({ tab: t, section: sec });
        });
      } else if (`${t.label} ${t.sidebarSubtitle || ''}`.toLowerCase().includes(q)) {
        uit.push({ tab: t, section: null });
      }
    });
    return uit;
  }, [zoek, TABS]);
  const [tabKey, setTabKey] = useState(() => {
    const t = TABS.find(x => x.key === initial.tab);
    if (t) return t.key;
    return beperkt ? (TABS[0]?.key || 'feedback') : 'users';
  });
  const [sectionKey, setSectionKey] = useState(() => {
    const t = TABS.find(x => x.key === initial.tab) || (beperkt ? TABS[0] : null);
    if (t?.sections && initial.section && t.sections.some(s => s.key === initial.section)) {
      return initial.section;
    }
    // Eén zichtbare sectie (bv. alleen ikwilwerken@): meteen openen in plaats van een overzicht met één kaart.
    if (beperkt && t?.sections?.length === 1) return t.sections[0].key;
    return null;
  });

  useEffect(() => {
    pushDeeplink(tabKey, sectionKey);
  }, [tabKey, sectionKey]);

  // Cross-section navigation (e.g. "Logs bekijken" vanuit Render servers).
  useEffect(() => {
    const onNavigate = (e) => {
      const { tab, section } = e.detail || {};
      const t = beperkteTabs(TOP_TABS, permissions).find(x => x.key === tab);
      if (!t) return;
      setTabKey(tab);
      setSectionKey(t.sections && section && t.sections.some(s => s.key === section) ? section : null);
    };
    window.addEventListener('ff-admin-navigate', onNavigate);
    return () => window.removeEventListener('ff-admin-navigate', onNavigate);
  }, [permissions]);

  const gaNaarTreffer = (tr) => {
    setTabKey(tr.tab.key);
    setSectionKey(tr.section ? tr.section.key : null);
    setZoek('');
  };
  const activeTab = TABS.find(t => t.key === tabKey) || TABS[0];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress size={22} sx={{ color: T.outlineVariant }} />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', bgcolor: T.bg, minHeight: 'calc(100vh - 44px)' }}>
      {/* Header — gedeelde PageHeader, accent volgt actieve tab */}
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 } }}>
        <PageHeader
          icon="settings"
          title="Beheer"
          subtitle="Gebruikers, applicatie-instellingen en feedback"
          accent={activeTab.accent}
        />
      </Box>

      {/* Top tabs — full bleed */}
      <Box sx={{
        width: '100%', borderBottom: `1px solid ${T.surfaceHigh}`,
        bgcolor: T.surface, mb: { xs: 2, md: 3 },
      }}>
        <Box sx={{
          display: 'flex', gap: 0.5, px: { xs: 2, sm: 3, md: 5 },
          overflowX: 'auto', alignItems: 'center',
        }}>
          {/* Zoeken over alle tabs en secties (wiki-feedback zAdmin page, 9 sep):
              typ een woord, kies een trefferkaart, en je staat in de juiste sectie. */}
          <Box sx={{ position: 'relative', mr: 1, flexShrink: 0 }}>
            <input
              value={zoek}
              onChange={(e) => setZoek(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Escape') setZoek(''); if (e.key === 'Enter' && zoekTreffers[0]) gaNaarTreffer(zoekTreffers[0]); }}
              placeholder="Zoek instelling…"
              aria-label="Zoek in admin"
              style={{ fontFamily: T.fontBody, fontSize: '0.85rem', padding: '7px 10px', borderRadius: 8, border: `1px solid ${T.outlineVariant}`, width: 220, background: T.surface, color: T.text }}
            />
            {zoek.trim() && (
              <Box sx={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, minWidth: 360, maxHeight: 380, overflowY: 'auto',
                bgcolor: T.surface, border: `1px solid ${T.outlineVariant}`, borderRadius: 2, boxShadow: '0 8px 24px rgba(15,23,42,.12)', py: 0.5 }}>
                {zoekTreffers.length === 0 ? (
                  <Typography sx={{ px: 1.5, py: 1, fontFamily: T.fontBody, fontSize: '0.82rem', color: T.secondary }}>Niets gevonden.</Typography>
                ) : zoekTreffers.slice(0, 12).map((tr) => (
                  <Box key={`${tr.tab.key}-${tr.section?.key || 'flat'}`} onClick={() => gaNaarTreffer(tr)}
                    sx={{ px: 1.5, py: 0.9, cursor: 'pointer', '&:hover': { bgcolor: T.surfaceLow } }}>
                    <Typography sx={{ fontFamily: T.font, fontSize: '0.85rem', fontWeight: 700, color: T.text }}>
                      {tr.section?.label || tr.tab.label}
                      <Typography component="span" sx={{ fontFamily: T.fontBody, fontSize: '0.72rem', color: T.secondary, ml: 1 }}>{tr.tab.label}</Typography>
                    </Typography>
                    {tr.section?.desc && <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.75rem', color: T.secondary }}>{tr.section.desc}</Typography>}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
          {TABS.map(t => {
            const active = t.key === tabKey;
            return (
              <Box
                key={t.key}
                onClick={() => { setTabKey(t.key); setSectionKey(null); }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1,
                  px: 2, py: 1.5, cursor: 'pointer', userSelect: 'none',
                  borderBottom: active ? `2px solid ${t.accent.color}` : '2px solid transparent',
                  color: active ? t.accent.color : T.secondary,
                  fontFamily: T.font, fontSize: '0.88rem',
                  fontWeight: active ? 700 : 600, letterSpacing: '-0.005em',
                  transition: 'color 0.12s, border-color 0.12s',
                  '&:hover': { color: active ? t.accent.color : T.text },
                }}
              >
                <Icon name={t.icon} size={18} fill={active} />
                {t.label}
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* Tab content — full bleed, sidebar + grid menu */}
      <Box sx={{ width: '100%', px: { xs: 2, sm: 3, md: 5 }, pb: 6 }}>
        {activeTab.flat ? (
          <activeTab.flat />
        ) : (
          <AdminHub
            title={activeTab.sidebarTitle}
            subtitle={activeTab.sidebarSubtitle}
            sections={activeTab.sections}
            activeKey={sectionKey}
            onSelect={setSectionKey}
          />
        )}
      </Box>
    </Box>
  );
}
